import { type FC, useEffect, useRef } from 'react'

import { VaultMediaType } from '@api'
import { useUnit } from 'effector-react'

import { AudioSkeleton } from '@/dialogs/VaultMediaPreviewDialog/AudioSkeleton.tsx'
import { VideoPlayer } from '@/dialogs/VaultMediaPreviewDialog/VideoPlayer.tsx'
import { VideoSkeleton } from '@/dialogs/VaultMediaPreviewDialog/VideoSkeleton.tsx'
import { $vaultMediasMap } from '@/models/vault/medias'
import { $mediaFailedAssets } from '@/models/vault/medias/errors.ts'
import { $mediaSource } from '@/models/vault/medias/selection.ts'
import {
  $streamingError,
  getStreamingUrlFx,
} from '@/models/vault/medias/streams.ts'
import { stopEventPropagation } from '@/utils/helpers.ts'

export interface Props {
  mediaId: number
}

export const VaultMediaContent: FC<Props> = ({ mediaId }) => {
  const vaultMediasMap = useUnit($vaultMediasMap)
  const media = vaultMediasMap[mediaId]
  const mediaFailedAssets = useUnit($mediaFailedAssets)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const mediaSource = useUnit($mediaSource)
  const isLoadingMediaSource = useUnit(getStreamingUrlFx.pending)
  const streamingError = useUnit($streamingError)
  const isVideo =
    media?.type === VaultMediaType.VIDEO ||
    (media?.type === VaultMediaType.GIF &&
      media.files.full.url?.includes('.mp4'))
  const isAudio = media?.type === VaultMediaType.AUDIO

  useEffect(() => {
    if (media) {
      if (mediaSource && !isLoadingMediaSource) {
        if (media.type === VaultMediaType.AUDIO) {
          audioRef.current?.load()
        }
      }
    }
  }, [isLoadingMediaSource, mediaSource, media])

  if (media?.type === VaultMediaType.PHOTO) {
    return !media.files.full.url || mediaFailedAssets[media.files.full.url] ? (
      <div className="bg-neutral-contrast dark:bg-card w-[450px] p-6 text-center">
        OnlyFans service restrict this media by reason:
        <br />
        Failed to load media file 😢
      </div>
    ) : (
      <img
        key={media.id}
        onClick={stopEventPropagation}
        className="max-h-[calc(100vh-32px)] max-w-[calc(100%-96px)]"
        src={media.files.full.url}
        data-media-id={media.id}
        // reset original url
        data-original-url=""
        alt=""
      />
    )
  }

  if (
    media?.type === VaultMediaType.GIF &&
    media.files.full.url?.includes('.gif')
  ) {
    return (
      <img
        onClick={stopEventPropagation}
        className="max-h-[calc(100vh-64px)] max-w-[calc(100%-96px)]"
        src={media.files.full.url}
        data-media-id={media.id}
        alt=""
      />
    )
  }

  if (isAudio) {
    return (
      <div className="flex w-full items-center justify-center">
        {streamingError ? (
          <div className="bg-neutral-contrast dark:bg-card w-[450px] p-6 text-center">
            {streamingError.body?.detail ? (
              <>
                OnlyFans service restrict this media by reason:
                <br />
                {streamingError.body?.detail}
              </>
            ) : (
              'Failed to load audio file 😢'
            )}
          </div>
        ) : isLoadingMediaSource || !mediaSource ? (
          <AudioSkeleton />
        ) : (
          <audio
            onClick={stopEventPropagation}
            ref={audioRef}
            className="w-8/12"
            controls
            controlsList="nodownload"
            onContextMenu={(event) => event.preventDefault()}
            src={mediaSource}
            data-media-id={media.id}
          />
        )}
      </div>
    )
  }

  if (isVideo) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        {streamingError ? (
          <div className="bg-neutral-contrast dark:bg-card w-[450px] p-6 text-center">
            {streamingError.body?.detail ? (
              <>
                OnlyFans service restrict this media by reason:
                <br />
                {streamingError.body?.detail}
              </>
            ) : (
              'Failed to load video file 😢'
            )}
          </div>
        ) : isLoadingMediaSource || !mediaSource ? (
          <VideoSkeleton />
        ) : (
          <VideoPlayer
            className="max-h-[calc(100%-100px)] max-w-[calc(100%-100px)]"
            src={mediaSource}
            mediaId={media.id}
          />
        )}
      </div>
    )
  }
}
